import { useQuery } from '@tanstack/react-query'
import { alertsApi } from '@/lib/friday-api'
import { useTasks } from '@/hooks/use-tasks'
import { useInboxCards, inboxWhisper } from '@/hooks/use-inbox'
import { useIntakeQueue } from '@/hooks/use-intake'
import { useLoaItems } from '@/hooks/use-loa'
import { useMeetings } from '@/hooks/use-meetings'
import { isOverdue } from '@/lib/task-utils'
import type { Alert } from '@/types/api'

/** A whisper badge: a count with a tone. Red = act now, soft = worth a look. */
export interface NavBadge {
  count: number
  tone: 'red' | 'soft'
  label: string
}

export type NavBadgeMap = Record<string, NavBadge>

const DAY = 86400

function daysUntil(epochSeconds?: number): number | null {
  if (epochSeconds == null) return null
  return Math.ceil((epochSeconds - Date.now() / 1000) / DAY)
}

/** Per-tab counts for the Inbox hub — replies, intake, alerts and broken automations. */
export interface InboxCounts {
  replies: number
  repliesUrgent: boolean
  intake: number
  alerts: number
  alertsUrgent: boolean
  /** Automations that failed or died mid-run in the last 24 hours. */
  failedRuns: number
  /** Combined attention count for the sidebar Inbox entry. */
  total: number
  urgent: boolean
}

/**
 * The counts behind the Inbox hub, shared by the sidebar (summed into one
 * whisper) and the hub itself (per-tab badges) so the logic lives in one place.
 */
export function useInboxCounts(): InboxCounts {
  const { data: inboxCards } = useInboxCards()
  const { data: intake } = useIntakeQueue()
  const { data: alertsData } = useQuery({ queryKey: ['alerts'], queryFn: alertsApi.list })

  const whisper = inboxWhisper(inboxCards)
  const intakePending = (intake ?? []).filter((i) => i.status === 'pending').length
  const alerts: Alert[] = alertsData?.alerts ?? []
  const alertsUrgent = alerts.some((a) => {
    const d = daysUntil(a.expires_at ?? a.end_date)
    return d != null && d <= 7
  })

  // Broken automations are counted off the alert feed rather than a second request:
  // failed and stuck runs already surface there, so the two can never disagree.
  const failedRuns = alerts.filter(
    (a) => a.type === 'automation_failed' || a.type === 'automation_stuck',
  ).length

  const total = whisper.count + intakePending + alerts.length
  return {
    replies: whisper.count,
    repliesUrgent: whisper.urgent,
    intake: intakePending,
    alerts: alerts.length,
    alertsUrgent,
    failedRuns,
    total,
    urgent: whisper.urgent || alertsUrgent,
  }
}

/**
 * Whisper-level counts for the nav, keyed by route. Counts only, no modals, no
 * pulses. Red is reserved for act-now (Inbox urgent, overdue tasks, authorities
 * inside their window); everything else whispers soft.
 */
export function useNavBadges(): NavBadgeMap {
  const { data: tasks } = useTasks()
  const { data: loa } = useLoaItems()
  const { data: meetings } = useMeetings('unlinked')
  const inbox = useInboxCounts()

  const badges: NavBadgeMap = {}

  const overdue = (tasks ?? []).filter((t) => isOverdue(t)).length
  if (overdue > 0) {
    badges['/tasks'] = { count: overdue, tone: 'red', label: `${overdue} overdue` }
  }

  // Inbox now carries intake + alerts as tabs, so its sidebar whisper is the sum.
  if (inbox.total > 0) {
    badges['/inbox'] = {
      count: inbox.total,
      tone: inbox.urgent ? 'red' : 'soft',
      label: `${inbox.total} waiting in your inbox`,
    }
  }

  const unlinkedMeetings = (meetings ?? []).length
  if (unlinkedMeetings > 0) {
    // Meetings now lives as a tab under Calendar, so the whisper rides the Calendar entry.
    badges['/calendar'] = {
      count: unlinkedMeetings,
      tone: 'soft',
      label: `${unlinkedMeetings} to link up`,
    }
  }

  const loaLive = (loa ?? []).filter(
    (l) => l.status === 'expiring_soon' || l.status === 'expired' || l.status === 'pending',
  )
  if (loaLive.length > 0) {
    const actNow = loaLive.some((l) => l.status === 'expiring_soon' || l.status === 'expired')
    // Authorities now lives as a tab under Contracts & Authorities.
    badges['/contracts'] = {
      count: loaLive.length,
      tone: actNow ? 'red' : 'soft',
      label: `${loaLive.length} need a look`,
    }
  }

  return badges
}
